import React, { useState, useEffect, useRef } from "react";
import { Play, Pause, SkipForward, Volume2, Music } from "lucide-react";

const TRACKS = [
  {
    title: "Rainy Evening 🌧",
    url: "https://raw.githubusercontent.com/bradtraversy/ambient-sound-mixer/main/audio/rain.mp3"
  },
  {
    title: "Cozy Fireplace 🪵",
    url: "https://raw.githubusercontent.com/bradtraversy/ambient-sound-mixer/main/audio/fireplace.mp3"
  },
  {
    title: "Forest Birds 🐦",
    url: "https://raw.githubusercontent.com/bradtraversy/ambient-sound-mixer/main/audio/birds.mp3"
  },
  {
    title: "Cafe Chatter ☕",
    url: "https://raw.githubusercontent.com/bradtraversy/ambient-sound-mixer/main/audio/cafe.mp3"
  },
  {
    title: "Whispering Wind 💨",
    url: "https://raw.githubusercontent.com/bradtraversy/ambient-sound-mixer/main/audio/wind.mp3"
  },
  {
    title: "Ocean Waves 🌊",
    url: "https://raw.githubusercontent.com/bradtraversy/ambient-sound-mixer/main/audio/ocean.mp3"
  },
  {
    title: "Midnight Insects 🌃",
    url: "https://raw.githubusercontent.com/bradtraversy/ambient-sound-mixer/main/audio/night.mp3"
  },
  {
    title: "Rolling Thunder ⛈",
    url: "https://raw.githubusercontent.com/bradtraversy/ambient-sound-mixer/main/audio/thunder.mp3"
  }
];

const MusicWidget = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [trackIndex, setTrackIndex] = useState(0);
  const [volume, setVolume] = useState(0.4);
  const audioRef = useRef(null);

  // Initialize and update track
  useEffect(() => {
    // Stop and clean up any existing audio playing
    if (audioRef.current) {
      audioRef.current.pause();
    }

    const audio = new Audio(TRACKS[trackIndex].url);
    audio.loop = true;
    audio.volume = volume;
    audioRef.current = audio;

    if (isPlaying) {
      audio.play().catch(err => {
        console.log("Audio play error (requires user interaction first):", err);
        setIsPlaying(false);
      });
    }

    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
      }
    };
  }, [trackIndex]);

  // Synchronize volume slider changes
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume;
    }
  }, [volume]);

  // Handle play/pause action
  const togglePlay = () => {
    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.play()
        .then(() => setIsPlaying(true))
        .catch(err => {
          console.log("Audio play failed:", err);
        });
    }
  };

  // Next Track action
  const playNext = () => {
    setTrackIndex((prev) => (prev + 1) % TRACKS.length);
    setIsPlaying(true);
  };

  return (
    <div className="bg-dark-card/85 backdrop-blur-xl border border-dark-border/60 rounded-2xl p-3.5 shadow-xl w-64 text-dark-text-primary select-none pointer-events-auto transition-all duration-300">
      <div className="flex items-center gap-3">
        {/* Disc/Album Cover Icon */}
        <div className={`w-10 h-10 rounded-full bg-cozy-orange/20 flex items-center justify-center text-cozy-orange ${
          isPlaying ? "animate-spin [animation-duration:8s]" : ""
        }`}>
          <Music className="w-4.5 h-4.5" />
        </div>

        {/* Track Title */}
        <div className="flex-1 min-w-0">
          <p className="text-[9px] font-bold text-cozy-orange uppercase tracking-wider">Ambient sounds</p>
          <p className="text-xs font-semibold text-dark-text-primary truncate capitalize mt-0.5">
            {TRACKS[trackIndex].title}
          </p>
        </div>
      </div>

      {/* Visual Audio Wave */}
      <div className="flex items-end gap-0.5 h-3 mt-3 px-1">
        <div 
          className={`w-1 bg-cozy-orange rounded-t-sm transition-all duration-200 ${isPlaying ? "animate-[bounce_0.8s_infinite_0.1s]" : ""}`} 
          style={{ height: isPlaying ? "40%" : "20%" }} 
        />
        <div 
          className={`w-1 bg-cozy-orange rounded-t-sm transition-all duration-200 ${isPlaying ? "animate-[bounce_0.8s_infinite_0.3s]" : ""}`} 
          style={{ height: isPlaying ? "75%" : "20%" }} 
        />
        <div 
          className={`w-1 bg-cozy-orange rounded-t-sm transition-all duration-200 ${isPlaying ? "animate-[bounce_0.8s_infinite_0.2s]" : ""}`} 
          style={{ height: isPlaying ? "50%" : "20%" }} 
        />
        <div 
          className={`w-1 bg-cozy-orange rounded-t-sm transition-all duration-200 ${isPlaying ? "animate-[bounce_0.8s_infinite_0.5s]" : ""}`} 
          style={{ height: isPlaying ? "90%" : "20%" }} 
        />
        <div 
          className={`w-1 bg-cozy-orange rounded-t-sm transition-all duration-200 ${isPlaying ? "animate-[bounce_0.8s_infinite_0.4s]" : ""}`} 
          style={{ height: isPlaying ? "60%" : "20%" }} 
        />
        <div 
          className={`w-1 bg-cozy-orange rounded-t-sm transition-all duration-200 ${isPlaying ? "animate-[bounce_0.8s_infinite_0.6s]" : ""}`} 
          style={{ height: isPlaying ? "35%" : "20%" }} 
        />
      </div>

      {/* Controls row */}
      <div className="flex items-center justify-between gap-4 mt-3 border-t border-dark-border/40 pt-2.5">
        <div className="flex items-center gap-2">
          <button
            onClick={togglePlay}
            className="p-1.5 rounded-full hover:bg-white/5 text-dark-text-secondary hover:text-dark-text-primary transition duration-150 cursor-pointer"
            aria-label={isPlaying ? "Pause Music" : "Play Music"}
          >
            {isPlaying ? <Pause className="w-4 h-4 fill-current" /> : <Play className="w-4 h-4 fill-current" />}
          </button>
          
          <button
            onClick={playNext}
            className="p-1.5 rounded-full hover:bg-white/5 text-dark-text-secondary hover:text-dark-text-primary transition duration-150 cursor-pointer"
            aria-label="Next Track"
          >
            <SkipForward className="w-4 h-4 fill-current" />
          </button>
        </div>

        {/* Volume controls */}
        <div className="flex items-center gap-2 flex-1 max-w-[100px]">
          <Volume2 className="w-3.5 h-3.5 text-dark-text-secondary" />
          <input
            type="range"
            min="0"
            max="1"
            step="0.05"
            value={volume}
            onChange={(e) => setVolume(parseFloat(e.target.value))}
            className="w-full accent-cozy-orange h-1 bg-white/10 rounded-lg cursor-pointer"
          />
        </div>
      </div>
    </div>
  );
};

export default MusicWidget;
